import { useCallback, useEffect, useMemo, useState } from "react";

import {
  anclar,
  cambiarSupuesto,
  cerrarAncla,
  declararCampana,
  listarAnclas,
  listarSupuestos,
  listarTerritorio,
  verCampana,
  verVotosPorSeccion,
  type Ancla,
  type Campana,
  type CampanaNueva,
  type CampanaVista,
  type Contienda,
  type MetaVotosSeccion,
  type NodoTerritorio,
  type ResumenVotos,
  type Supuesto,
  type Yo,
} from "./api";
import { asignables as soloAsignables, caminos } from "./arbol";
import { Cargando, Cifra, ErrorDeCarga, Insignia, Pantalla, Tabla, Vacio, type Columna } from "./componentes";
import { fecha, haceCuanto, numero, plural } from "./formato";
import { mensajeDeError } from "./mensajes";
import type { Navegar } from "./navegacion";

/**
 * SUPUESTOS: la campaña, los cuatro supuestos de «votos para ganar», las
 * anclas y la meta sección por sección con su origen.
 *
 * Es la pantalla donde la dirección decide con números: ratifica la
 * participación (con lo que de verdad hubo la última vez, propuesto), ajusta
 * el margen o el techo con motivo, y fija a mano la meta de una sección
 * cuando la conoce mejor que la fórmula. Nada se edita en sitio: cambiar un
 * supuesto cierra el vigente y abre otro, y el historial queda.
 */

export const CONTIENDA: Record<Contienda, string> = {
  ayuntamiento: "Ayuntamiento",
  diputacion_local: "Diputación local",
  diputacion_federal: "Diputación federal",
  gubernatura: "Gubernatura",
  senaduria: "Senaduría",
  presidencia: "Presidencia",
};

const SUPUESTO: Record<Supuesto["clave"], { nombre: string; ayuda: string }> = {
  participacion: {
    nombre: "Participación",
    ayuda: "Qué parte de la lista nominal va a votar (%). Define los votos emitidos esperados.",
  },
  margen: {
    nombre: "Margen",
    ayuda: "Puntos porcentuales de los emitidos esperados que se suman sobre el rival.",
  },
  techo: {
    nombre: "Techo",
    ayuda: "Ninguna meta rebasa este % de los emitidos esperados: lo que no cabe, no se pide.",
  },
  piso: {
    nombre: "Piso",
    ayuda: "La meta nunca baja de los votos propios de la última elección más este %.",
  },
};

const ORIGEN: Record<MetaVotosSeccion["origen"], { texto: string; tono: "exito" | "aviso" | "neutro" | "primario" }> = {
  calculada: { texto: "calculada", tono: "exito" },
  anclada: { texto: "anclada", tono: "primario" },
  sin_base: { texto: "sin base", tono: "aviso" },
  sin_parametros: { texto: "sin parámetros", tono: "neutro" },
};

export function Supuestos({
  yo,
  parametros,
  navegar,
  avisar,
}: {
  yo: Yo;
  parametros: Record<string, unknown>;
  navegar: Navegar;
  avisar: (t: string) => void;
}) {
  const [vista, setVista] = useState<CampanaVista | null>(null);
  const [supuestos, setSupuestos] = useState<{ vigentes: Supuesto[]; historial: Supuesto[] } | null>(null);
  const [anclas, setAnclas] = useState<Ancla[]>([]);
  const [votos, setVotos] = useState<{ resumen: ResumenVotos; secciones: MetaVotosSeccion[] } | null>(null);
  const [nodos, setNodos] = useState<NodoTerritorio[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editandoCampana, setEditandoCampana] = useState(false);
  const [borrador, setBorrador] = useState<CampanaNueva | null>(null);
  const [cambio, setCambio] = useState<{ clave: Supuesto["clave"]; valor: string; motivo: string } | null>(null);
  const [nuevaAncla, setNuevaAncla] = useState<{ seccion: string; votos: string; motivo: string } | null>(null);
  const [filtroOrigen, setFiltroOrigen] = useState<string>(
    typeof parametros["ver"] === "string" ? (parametros["ver"] as string) : "",
  );
  const puedeAdministrar = Boolean(yo.capacidades["administrar_metas"]);
  const puedeAsignar = Boolean(yo.capacidades["administrar_usuarios"]);

  const cargar = useCallback(async () => {
    setError(null);
    try {
      const [c, s, a, v] = await Promise.all([verCampana(), listarSupuestos(), listarAnclas(), verVotosPorSeccion()]);
      setVista(c);
      setSupuestos(s);
      setAnclas(a.anclas);
      setVotos({ resumen: v.resumen, secciones: v.secciones });
      if (puedeAsignar) setNodos((await listarTerritorio()).nodos);
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }, [puedeAsignar]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const nombreDelNodo = useMemo(() => caminos(nodos), [nodos]);
  const raices = useMemo(() => soloAsignables(nodos).filter((n) => n.tipo !== "seccion"), [nodos]);

  function empezarCampana(c: Campana | null) {
    setBorrador({
      nombre: c?.nombre ?? "",
      contienda: c?.contienda ?? "ayuntamiento",
      partido: c?.partido ?? "",
      jornada: c?.jornada ?? "",
      territorio_id: c?.territorio_id ?? null,
      meta_votos_estructura: c?.meta_votos_estructura ?? null,
    });
    setEditandoCampana(true);
  }

  async function guardarCampana() {
    if (!borrador) return;
    setError(null);
    try {
      await declararCampana({
        ...borrador,
        partido: borrador.partido?.trim() || null,
        jornada: borrador.jornada || null,
      });
      avisar("Campaña declarada. Las metas se recalculan al leer.");
      setEditandoCampana(false);
      await cargar();
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }

  async function guardarSupuesto() {
    if (!cambio) return;
    const valor = Number(cambio.valor);
    if (!Number.isFinite(valor) || valor < 0 || valor > 100) {
      setError("El supuesto es un porcentaje entre 0 y 100.");
      return;
    }
    try {
      await cambiarSupuesto(cambio.clave, valor, cambio.motivo);
      avisar(`${SUPUESTO[cambio.clave].nombre} ahora es ${valor} %. El anterior quedó en el historial.`);
      setCambio(null);
      await cargar();
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }

  async function guardarAncla() {
    if (!nuevaAncla) return;
    const seccion = Number(nuevaAncla.seccion);
    const v = Number(nuevaAncla.votos);
    if (!Number.isInteger(seccion) || seccion < 1 || !Number.isInteger(v) || v < 1) {
      setError("Sección y votos tienen que ser enteros mayores que cero.");
      return;
    }
    try {
      await anclar(seccion, v, nuevaAncla.motivo);
      avisar(`Sección ${seccion} anclada en ${numero(v)} votos.`);
      setNuevaAncla(null);
      await cargar();
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }

  async function quitarAncla(a: Ancla) {
    const motivo = window.prompt(`¿Por qué se retira el ancla de la sección ${a.seccion}?`);
    if (!motivo || motivo.trim().length < 5) return;
    try {
      await cerrarAncla(a.id, motivo.trim());
      avisar(`Ancla de la sección ${a.seccion} retirada; vuelve la fórmula.`);
      await cargar();
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }

  if (error && !vista) {
    return (
      <Pantalla titulo="Supuestos">
        <ErrorDeCarga mensaje={error} reintentar={() => void cargar()} />
      </Pantalla>
    );
  }
  if (!vista || !supuestos || !votos) {
    return (
      <Pantalla titulo="Supuestos">
        <Cargando que="la campaña y sus supuestos" />
      </Pantalla>
    );
  }

  const c = vista.campana;
  const secciones = votos.secciones.filter((s) => !filtroOrigen || s.origen === filtroOrigen);
  const columnas: Columna<MetaVotosSeccion>[] = [
    { id: "seccion", titulo: "Sección", clave: (s) => s.seccion, celda: (s) => <strong className="usuario">{s.seccion}</strong> },
    {
      id: "origen",
      titulo: "Origen",
      clave: (s) => s.origen,
      celda: (s) => <Insignia tono={ORIGEN[s.origen].tono}>{ORIGEN[s.origen].texto}{s.topada ? " · topada" : ""}</Insignia>,
    },
    { id: "meta", titulo: "Votos para ganar", numero: true, clave: (s) => s.meta ?? -1, celda: (s) => (s.meta == null ? "—" : numero(s.meta)) },
    { id: "ln", titulo: "Lista nominal", numero: true, clave: (s) => s.lista_nominal ?? -1, celda: (s) => numero(s.lista_nominal) },
    {
      id: "rival",
      titulo: "Rival (última vez)",
      clave: (s) => s.base_rival ?? -1,
      celda: (s) =>
        s.base_rival == null ? (
          <span className="apagado">sin resultado</span>
        ) : (
          <>
            {s.rival} <span className="apagado">· {numero(s.base_rival)} en {s.anio_referencia}</span>
          </>
        ),
    },
    {
      id: "propios",
      titulo: "Propios (última vez)",
      numero: true,
      clave: (s) => s.propios_referencia ?? -1,
      celda: (s) =>
        s.propios_referencia == null ? (
          <span className="apagado">—</span>
        ) : (
          <>
            {numero(s.propios_referencia)} {s.gano_referencia ? <Insignia tono="exito">ganó</Insignia> : null}
          </>
        ),
    },
  ];

  return (
    <Pantalla
      titulo="Supuestos"
      descripcion="Con qué se calcula «votos para ganar»: la campaña, cuatro supuestos con historial y las metas fijadas a mano."
    >
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {/* ── La campaña ─────────────────────────────────────────── */}
      <section aria-labelledby="s-campana">
        <div className="encabezado">
          <h2 id="s-campana">La campaña</h2>
          {puedeAdministrar && !editandoCampana && (
            <button type="button" className="btn secundario" onClick={() => empezarCampana(c)}>
              {c ? "Cambiar" : "Declarar la campaña"}
            </button>
          )}
        </div>
        {!c && !editandoCampana && (
          <Vacio
            titulo="La campaña no está declarada"
            detalle="Sin contienda ni jornada el tablero no puede decir cuántos votos hacen falta ni cuántos días quedan."
          />
        )}
        {c && !editandoCampana && (
          <div className="cifras">
            <Cifra etiqueta="contienda" valor={CONTIENDA[c.contienda]} detalle={c.nombre} sinAccion="Es la campaña de esta instancia" />
            <Cifra
              etiqueta="jornada"
              valor={c.jornada ? fecha(c.jornada) : "—"}
              detalle={vista.dias_a_jornada != null ? plural(vista.dias_a_jornada, "día", "días") + " restantes" : "sin fecha"}
              sinAccion="La fecha de la elección"
            />
            <Cifra etiqueta="fuerza política" valor={c.partido ?? "—"} detalle={c.partido ? "los votos propios son los suyos" : "sin partido: el rival es el ganador"} sinAccion="Con quién se cuentan los votos propios" />
            <Cifra
              etiqueta="territorio"
              valor={c.territorio_nombre || (c.territorio_tipo ? `${c.territorio_tipo} ${c.territorio_clave}` : "todo el árbol")}
              detalle={plural(vista.secciones, "sección", "secciones")}
              alAbrir={() => navegar({ pantalla: "territorio", parametros: c.territorio_id ? { nodo: c.territorio_id } : {} })}
            />
            <Cifra
              etiqueta="votos de la estructura"
              valor={c.meta_votos_estructura != null ? numero(c.meta_votos_estructura) : "—"}
              detalle={c.meta_votos_estructura != null ? "lo que la campaña planea conseguir" : "sin meta declarada"}
              tono={c.meta_votos_estructura == null ? "aviso" : "neutro"}
              sinAccion="La meta que la estructura declara"
            />
          </div>
        )}
        {editandoCampana && borrador && (
          <div className="tarjeta formulario">
            <h3>{c ? "Cambiar la campaña" : "Declarar la campaña"}</h3>
            <div className="campos-en-fila">
              <div className="campo">
                <label htmlFor="c-nombre">Nombre</label>
                <input id="c-nombre" value={borrador.nombre} onChange={(e) => setBorrador({ ...borrador, nombre: e.target.value })} />
              </div>
              <div className="campo">
                <label htmlFor="c-contienda">Contienda</label>
                <select id="c-contienda" value={borrador.contienda} onChange={(e) => setBorrador({ ...borrador, contienda: e.target.value as Contienda })}>
                  {(Object.keys(CONTIENDA) as Contienda[]).map((k) => (
                    <option key={k} value={k}>{CONTIENDA[k]}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="campos-en-fila">
              <div className="campo">
                <label htmlFor="c-partido">Fuerza política (siglas)</label>
                <input id="c-partido" value={borrador.partido ?? ""} placeholder="p. ej. MC" onChange={(e) => setBorrador({ ...borrador, partido: e.target.value })} />
                <p className="ayuda">Tal como aparece en los resultados. Con ella se cuentan los votos propios; sin ella, el rival es quien ganó.</p>
              </div>
              <div className="campo">
                <label htmlFor="c-jornada">Jornada electoral</label>
                <input id="c-jornada" type="date" value={borrador.jornada ?? ""} onChange={(e) => setBorrador({ ...borrador, jornada: e.target.value })} />
              </div>
            </div>
            <div className="campos-en-fila">
              <div className="campo">
                <label htmlFor="c-territorio">Territorio de la campaña</label>
                <select
                  id="c-territorio"
                  value={borrador.territorio_id ?? ""}
                  onChange={(e) => setBorrador({ ...borrador, territorio_id: e.target.value === "" ? null : Number(e.target.value) })}
                >
                  <option value="">Todo el árbol</option>
                  {raices.map((n) => (
                    <option key={n.id} value={n.id}>{nombreDelNodo.get(n.id)}</option>
                  ))}
                </select>
              </div>
              <div className="campo">
                <label htmlFor="c-meta">Votos que planea la estructura</label>
                <input
                  id="c-meta"
                  inputMode="numeric"
                  value={borrador.meta_votos_estructura ?? ""}
                  onChange={(e) => setBorrador({ ...borrador, meta_votos_estructura: e.target.value === "" ? null : Number(e.target.value) })}
                />
                <p className="ayuda">El tablero la compara con los votos para ganar y enseña la brecha.</p>
              </div>
            </div>
            <div className="acciones">
              <button type="button" className="btn primario" onClick={() => void guardarCampana()}>Guardar</button>
              <button type="button" className="btn terciario" onClick={() => setEditandoCampana(false)}>Cancelar</button>
            </div>
          </div>
        )}
      </section>

      {/* ── Los supuestos ──────────────────────────────────────── */}
      <section aria-labelledby="s-supuestos">
        <h2 id="s-supuestos">Los cuatro supuestos</h2>
        {vista.supuestos_sin_ratificar.length > 0 && (
          <p className="ayuda">
            <Insignia tono="aviso">sin ratificar</Insignia>{" "}
            {vista.supuestos_sin_ratificar.length === 4
              ? "Los cuatro siguen siendo la semilla."
              : `Faltan por ratificar: ${vista.supuestos_sin_ratificar.map((k) => SUPUESTO[k as Supuesto["clave"]]?.nombre ?? k).join(", ")}.`}
            {vista.participacion_medida != null && (
              <> La participación que de verdad hubo la última vez en estas secciones fue <strong>{vista.participacion_medida} %</strong>.</>
            )}
          </p>
        )}
        <div className="cifras">
          {supuestos.vigentes.map((s) => {
            const esSemilla = s.motivo.startsWith("Semilla");
            const def = SUPUESTO[s.clave];
            return puedeAdministrar ? (
              <Cifra
                key={s.clave}
                etiqueta={def.nombre}
                valor={`${Number(s.valor)} %`}
                detalle={esSemilla ? "semilla sin ratificar" : `${s.motivo} · ${haceCuanto(s.desde)}`}
                tono={esSemilla ? "aviso" : "neutro"}
                alAbrir={() =>
                  setCambio({
                    clave: s.clave,
                    valor: String(s.clave === "participacion" && vista.participacion_medida != null && esSemilla ? vista.participacion_medida : Number(s.valor)),
                    motivo: "",
                  })
                }
              />
            ) : (
              <Cifra key={s.clave} etiqueta={def.nombre} valor={`${Number(s.valor)} %`} detalle={esSemilla ? "semilla sin ratificar" : s.motivo} sinAccion="Cambiarlos exige administrar metas" />
            );
          })}
        </div>
        {cambio && (
          <div className="tarjeta formulario">
            <h3>{SUPUESTO[cambio.clave].nombre}</h3>
            <p className="ayuda">{SUPUESTO[cambio.clave].ayuda}</p>
            <div className="campos-en-fila">
              <div className="campo">
                <label htmlFor="s-valor">Valor (%)</label>
                <input id="s-valor" inputMode="decimal" value={cambio.valor} onChange={(e) => setCambio({ ...cambio, valor: e.target.value })} />
              </div>
              <div className="campo">
                <label htmlFor="s-motivo">Motivo</label>
                <input id="s-motivo" value={cambio.motivo} placeholder="p. ej. ratificado en la mesa del 20/10" onChange={(e) => setCambio({ ...cambio, motivo: e.target.value })} />
                <p className="ayuda">Queda en el historial: es la respuesta a «¿por qué cambió la meta?».</p>
              </div>
            </div>
            <div className="acciones">
              <button type="button" className="btn primario" disabled={cambio.motivo.trim().length < 5} onClick={() => void guardarSupuesto()}>
                Cerrar el vigente y abrir este
              </button>
              <button type="button" className="btn terciario" onClick={() => setCambio(null)}>Cancelar</button>
            </div>
          </div>
        )}
        {supuestos.historial.some((h) => h.hasta) && (
          <details>
            <summary>Historial de supuestos</summary>
            <ul className="actividad">
              {supuestos.historial.map((h) => (
                <li key={h.id}>
                  <strong>{SUPUESTO[h.clave].nombre}</strong> {Number(h.valor)} % · {fecha(h.desde)}
                  {h.hasta ? ` → ${fecha(h.hasta)}` : " → vigente"} · {h.motivo}
                  {h.quien ? <span className="apagado"> · {h.quien}</span> : null}
                </li>
              ))}
            </ul>
          </details>
        )}
      </section>

      {/* ── Las anclas ─────────────────────────────────────────── */}
      <section aria-labelledby="s-anclas">
        <div className="encabezado">
          <h2 id="s-anclas">Metas fijadas a mano</h2>
          {puedeAdministrar && !nuevaAncla && (
            <button type="button" className="btn secundario" onClick={() => setNuevaAncla({ seccion: "", votos: "", motivo: "" })}>
              Anclar una sección
            </button>
          )}
        </div>
        {nuevaAncla && (
          <div className="tarjeta formulario">
            <div className="campos-en-fila">
              <div className="campo">
                <label htmlFor="a-seccion">Sección</label>
                <input id="a-seccion" inputMode="numeric" value={nuevaAncla.seccion} onChange={(e) => setNuevaAncla({ ...nuevaAncla, seccion: e.target.value })} />
              </div>
              <div className="campo">
                <label htmlFor="a-votos">Votos para ganar</label>
                <input id="a-votos" inputMode="numeric" value={nuevaAncla.votos} onChange={(e) => setNuevaAncla({ ...nuevaAncla, votos: e.target.value })} />
              </div>
              <div className="campo">
                <label htmlFor="a-motivo">Motivo</label>
                <input id="a-motivo" value={nuevaAncla.motivo} onChange={(e) => setNuevaAncla({ ...nuevaAncla, motivo: e.target.value })} />
              </div>
            </div>
            <div className="acciones">
              <button type="button" className="btn primario" disabled={nuevaAncla.motivo.trim().length < 5} onClick={() => void guardarAncla()}>Anclar</button>
              <button type="button" className="btn terciario" onClick={() => setNuevaAncla(null)}>Cancelar</button>
            </div>
          </div>
        )}
        {anclas.filter((a) => !a.hasta).length === 0 ? (
          <p className="apagado">Ninguna sección anclada: todas las metas salen de la fórmula.</p>
        ) : (
          <ul className="actividad">
            {anclas.filter((a) => !a.hasta).map((a) => (
              <li key={a.id}>
                <strong className="usuario">{a.seccion}</strong> · {numero(a.votos)} votos · {a.motivo}
                <span className="apagado"> · {haceCuanto(a.desde)}{a.quien ? ` · ${a.quien}` : ""}</span>
                {puedeAdministrar && (
                  <>
                    {" "}
                    <button type="button" className="btn terciario fila" onClick={() => void quitarAncla(a)}>Retirar</button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ── La meta por sección ────────────────────────────────── */}
      <section aria-labelledby="s-metas">
        <div className="encabezado">
          <h2 id="s-metas">Votos para ganar, sección por sección</h2>
          <span className="apagado chico">
            {numero(votos.resumen.votos_para_ganar)} en {votos.resumen.con_meta} de {votos.resumen.secciones} secciones
            {votos.resumen.sin_base ? ` · ${votos.resumen.sin_base} sin base` : ""}
            {votos.resumen.topadas ? ` · ${votos.resumen.topadas} topadas` : ""}
            {votos.resumen.ancladas ? ` · ${votos.resumen.ancladas} ancladas` : ""}
          </span>
        </div>
        <div className="herramientas">
          <fieldset className="segmentos" aria-label="Mostrar">
            <legend className="solo-lectores">Mostrar</legend>
            {(
              [
                ["", "Todas"],
                ["calculada", "Calculadas"],
                ["anclada", "Ancladas"],
                ["sin_base", "Sin base"],
                ["sin_parametros", "Sin parámetros"],
              ] as [string, string][]
            ).map(([f, t]) => (
              <label key={f} className={filtroOrigen === f ? "activo" : undefined}>
                <input type="radio" name="origen" value={f} checked={filtroOrigen === f} onChange={() => setFiltroOrigen(f)} />
                {t}
              </label>
            ))}
          </fieldset>
        </div>
        <Tabla
          columnas={columnas}
          filas={secciones}
          claveDe={(s) => s.seccion}
          caption="La meta de cada sección y de dónde sale. Toca una para verla en el mapa."
          ordenInicial={{ columna: "meta", desc: true }}
          alTocarFila={(s) => navegar({ pantalla: "mapa", parametros: { seccion: s.seccion, capa: "meta" } })}
          vacio="Ninguna sección en este filtro."
        />
      </section>
    </Pantalla>
  );
}
